import React, { useState, useMemo } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Typography,
  Chip,
  Paper,
  Button,
  Menu
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  CheckCircle,
  RadioButtonUnchecked,
  Close
} from "@mui/icons-material";
import { MbscCalendarEvent } from "@mobiscroll/react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../hooks/useScreenSize";
import { focusRingStyles, srOnlyStyles } from "../utils/accessibility";

interface RequiredSlotsValidationProps {
  totalSlots: number;
  availableDaysRequired: number;
  minSlotsAcrossDays: number;
  currentSlots: MbscCalendarEvent[];
  timezone: string;
  duration: number;
  inline?: boolean;
  validDates?: string[]; // Add validDates prop
  setIsAvailabilityParametersSatisfied: (isValid: boolean) => void; // Optional callback to inform parent about validation status
}

export const RequiredSlotsValidation: React.FC<
  RequiredSlotsValidationProps
> = ({
  totalSlots,
  availableDaysRequired,
  minSlotsAcrossDays,
  currentSlots,
  timezone,
  duration,
  inline = false,
  validDates = [], // Add default empty array
  setIsAvailabilityParametersSatisfied
}) => {
  const { t } = useTranslation();
  const { isLargeScreen } = useScreenSize();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const open = Boolean(anchorEl);

  const validation = useMemo(() => {
    // Apply validation logic to ensure consistency with API operations
    // If any parameter is defined, ensure all parameters have minimum value of 1
    const hasAnyParam =
      (totalSlots !== undefined && totalSlots !== null && totalSlots > 0) ||
      (availableDaysRequired !== undefined &&
        availableDaysRequired !== null &&
        availableDaysRequired > 0) ||
      (minSlotsAcrossDays !== undefined &&
        minSlotsAcrossDays !== null &&
        minSlotsAcrossDays > 0);

    const effectiveTotalSlots = hasAnyParam
      ? Math.max(1, totalSlots || 1)
      : totalSlots || 0;
    const effectiveAvailableDaysRequired = hasAnyParam
      ? Math.max(1, availableDaysRequired || 1)
      : availableDaysRequired || 0;
    const effectiveMinSlotsAcrossDays = hasAnyParam
      ? Math.max(1, minSlotsAcrossDays || 1)
      : minSlotsAcrossDays || 0;

    // Count unique days with slots and calculate slots per day based on duration
    const daySlotCounts: Record<string, number> = {};

    currentSlots.forEach((slot) => {
      const day = moment(slot.start).tz(timezone).format("YYYY-MM-DD");

      // Only consider slots that are within valid date ranges
      if (validDates.length > 0 && !validDates.includes(day)) {
        return; // Skip this slot if it's not in valid dates
      }

      // Calculate how many slots this event provides based on duration
      const eventDurationMinutes = moment(slot.end).diff(
        moment(slot.start),
        "minutes"
      );
      const slotsProvided = Math.floor(eventDurationMinutes / duration);

      daySlotCounts[day] = (daySlotCounts[day] || 0) + slotsProvided;
    });

    const uniqueDays = Object.keys(daySlotCounts);
    const totalSlotsCount = Object.values(daySlotCounts).reduce(
      (sum, count) => sum + count,
      0
    );
    const uniqueDaysCount = uniqueDays.length;

    const hasTotalSlots = totalSlotsCount >= effectiveTotalSlots;
    const hasRequiredDays = uniqueDaysCount >= effectiveAvailableDaysRequired;

    // Check if each day has the minimum required slots
    // Sort slots per day in descending order to get the top days
    const slotsPerDayArray = Object.values(daySlotCounts).sort((a, b) => b - a);

    // Check if we have at least minSlotsAcrossDays for the required number of days
    const hasMinSlotsAcrossDays =
      totalSlotsCount === 0
        ? effectiveMinSlotsAcrossDays === 0
        : slotsPerDayArray
            .slice(0, effectiveAvailableDaysRequired)
            .every((count) => count >= effectiveMinSlotsAcrossDays);

    const allRequirementsMet =
      hasTotalSlots && hasRequiredDays && hasMinSlotsAcrossDays;

    // Calculate minimum slots for display across required days
    const minSlotsForDisplay =
      totalSlotsCount === 0
        ? 0
        : slotsPerDayArray.slice(0, effectiveAvailableDaysRequired).length > 0
          ? Math.min(
              ...slotsPerDayArray.slice(0, effectiveAvailableDaysRequired)
            )
          : 0;

    return {
      hasTotalSlots,
      hasRequiredDays,
      hasMinSlotsAcrossDays,
      allRequirementsMet,
      totalSlotsCount,
      uniqueDaysCount,
      slotsPerDay: daySlotCounts,
      minSlotsForDisplay,
      effectiveTotalSlots, // Include the calculated total slots for display
      effectiveAvailableDaysRequired, // Include for display
      effectiveMinSlotsAcrossDays // Include for display
    };
  }, [
    currentSlots,
    totalSlots,
    availableDaysRequired,
    minSlotsAcrossDays,
    timezone,
    duration,
    validDates
  ]);

  // Update parent state when validation changes (moved from useMemo to avoid side effects during render)
  React.useEffect(() => {
    setIsAvailabilityParametersSatisfied(validation.allRequirementsMet);
  }, [validation.allRequirementsMet, setIsAvailabilityParametersSatisfied]);

  // Set initial state to expanded when component mounts
  React.useEffect(() => {
    if (!isInitialized && totalSlots > 0) {
      // Use a small delay to ensure the button is rendered
      setTimeout(() => {
        setIsInitialized(true);
      }, 100);
    }
  }, [totalSlots, isInitialized]);

  // Don't render if total slots is 0, null, or undefined
  if (!totalSlots || totalSlots === 0) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (open) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${remainingMinutes}min`;
    }
    return `${minutes}min`;
  };

  const getStatusIcon = (isCompleted: boolean) => {
    return isCompleted ? (
      <>
        <CheckCircle
          sx={{ color: "success.main", fontSize: 20 }}
          aria-hidden="true"
        />
        <Box component="span" sx={srOnlyStyles}>
          {t("ROOSTER.PUBLIC-APP.REQUIREMENT_MET", {
            defaultValue: "Requirement met"
          })}
        </Box>
      </>
    ) : (
      <>
        <Close sx={{ color: "error.main", fontSize: 20 }} aria-hidden="true" />
        <Box component="span" sx={srOnlyStyles}>
          {t("ROOSTER.PUBLIC-APP.REQUIREMENT_NOT_MET", {
            defaultValue: "Requirement not met"
          })}
        </Box>
      </>
    );
  };

  if (inline) {
    // Render compact trigger button for ActionBar
    return (
      <Box>
        <Button
          id="timeslots-validation-trigger"
          size="small"
          onClick={handleClick}
          data-timeslots-button
          aria-expanded={open}
          aria-controls="timeslots-validation-menu"
          aria-haspopup="true"
          aria-label={
            validation.allRequirementsMet
              ? t("ROOSTER.PUBLIC-APP.TIMESLOTS_REQUIREMENTS_MET", {
                  defaultValue:
                    "Timeslot requirements met. Click to view details."
                })
              : t("ROOSTER.PUBLIC-APP.TIMESLOTS_REQUIREMENTS_NOT_MET", {
                  defaultValue:
                    "Timeslot requirements not met. Click to view details."
                })
          }
          sx={{
            borderLeft: validation.allRequirementsMet
              ? "4px solid"
              : "4px solid",
            borderLeftColor: validation.allRequirementsMet
              ? "success.main"
              : "warning.main",
            borderRadius: 1,
            px: 1,
            ...focusRingStyles
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ textTransform: "none" }}
          >
            {isLargeScreen ? (
              t("ROOSTER.PUBLIC-APP.REQUIRED_TIMESLOTS", {
                defaultValue: "Requested Timeslots"
              })
            ) : (
              <>
                {t("ROOSTER.PUBLIC-APP.REQUIRED_TIMESLOTS_COMPACT", {
                  defaultValue: "Timeslots"
                })}
                <span style={{ color: "red" }} aria-hidden="true">
                  *
                </span>
              </>
            )}
          </Typography>
          <IconButton size="small" aria-hidden="true" tabIndex={-1}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Button>

        <Menu
          id="timeslots-validation-menu"
          aria-labelledby="timeslots-validation-trigger"
          anchorEl={anchorEl}
          open={open}
          onClose={(_, reason) => {
            // Only close on button click or ESC, not on backdrop click
            if (reason === "escapeKeyDown") {
              setAnchorEl(null);
            }
          }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          disableScrollLock
          slotProps={{
            backdrop: {
              invisible: true,
              // Ensure touch events pass through
              sx: {
                pointerEvents: "none",
                touchAction: "auto"
              }
            },
            paper: {
              sx: {
                minWidth: 280,
                maxWidth: 350,
                mt: -1,
                pointerEvents: "auto",
                touchAction: "auto"
              },
              onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
              onTouchMove: (e: React.TouchEvent) => e.stopPropagation(),
              onTouchEnd: (e: React.TouchEvent) => e.stopPropagation()
            },
            root: {
              style: {
                pointerEvents: "none",
                touchAction: "auto" // Allow touch events to pass through the root
              }
            }
          }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center"
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center"
          }}
          MenuListProps={{
            disablePadding: true,
            onKeyDown: (e) => {
              // Allow ESC to close
              if (e.key === "Escape") {
                setAnchorEl(null);
              }
            }
          }}
        >
          <Box
            sx={{ p: 2 }}
            role="region"
            aria-label={t("ROOSTER.PUBLIC-APP.TIMESLOTS_VALIDATION_STATUS", {
              defaultValue: "Timeslot validation status"
            })}
          >
            {/* Header with close button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={600}
                id="timeslots-validation-title"
              >
                {t("ROOSTER.PUBLIC-APP.REQUIRED_TIMESLOTS", {
                  defaultValue: "Requested timeslots"
                })}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setAnchorEl(null)}
                aria-label={t("ROOSTER.PUBLIC-APP.CLOSE", {
                  defaultValue: "Close"
                })}
                sx={focusRingStyles}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Stack spacing={1} role="list" aria-live="polite">
              {/* Total Slots */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                role="listitem"
              >
                <Box aria-hidden="true">
                  {getStatusIcon(validation.hasTotalSlots)}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t("ROOSTER.PUBLIC-APP.TOTAL_SLOTS_LABEL", {
                    defaultValue: "{{slots}} x {{duration}} slots",
                    slots: validation.effectiveTotalSlots,
                    duration: formatDuration(duration)
                  })}
                </Typography>
                <Chip
                  size="small"
                  label={`${validation.totalSlotsCount}/${validation.effectiveTotalSlots}`}
                  color={validation.hasTotalSlots ? "success" : "error"}
                  variant="outlined"
                  aria-label={
                    validation.hasTotalSlots
                      ? t("ROOSTER.PUBLIC-APP.SLOTS_REQUIREMENT_MET", {
                          defaultValue: `${validation.totalSlotsCount} of ${validation.effectiveTotalSlots} slots selected, requirement met`
                        })
                      : t("ROOSTER.PUBLIC-APP.SLOTS_REQUIREMENT_NOT_MET", {
                          defaultValue: `${validation.totalSlotsCount} of ${validation.effectiveTotalSlots} slots selected, requirement not met`
                        })
                  }
                />
              </Box>

              {/* Available Days */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                role="listitem"
              >
                <Box aria-hidden="true">
                  {getStatusIcon(validation.hasRequiredDays)}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t("ROOSTER.PUBLIC-APP.DIFFERENT_DAYS_LABEL", {
                    defaultValue: "{{days}} different days",
                    days: validation.effectiveAvailableDaysRequired
                  })}
                </Typography>
                <Chip
                  size="small"
                  label={`${validation.uniqueDaysCount}/${validation.effectiveAvailableDaysRequired}`}
                  color={validation.hasRequiredDays ? "success" : "error"}
                  variant="outlined"
                  aria-label={
                    validation.hasRequiredDays
                      ? t("ROOSTER.PUBLIC-APP.DAYS_REQUIREMENT_MET", {
                          defaultValue: `${validation.uniqueDaysCount} of ${validation.effectiveAvailableDaysRequired} days selected, requirement met`
                        })
                      : t("ROOSTER.PUBLIC-APP.DAYS_REQUIREMENT_NOT_MET", {
                          defaultValue: `${validation.uniqueDaysCount} of ${validation.effectiveAvailableDaysRequired} days selected, requirement not met`
                        })
                  }
                />
              </Box>

              {/* Min Slots Across Required Days */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                role="listitem"
              >
                <Box aria-hidden="true">
                  {getStatusIcon(validation.hasMinSlotsAcrossDays)}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t("ROOSTER.PUBLIC-APP.SLOTS_PER_DAY_FOR_DAYS_LABEL", {
                    defaultValue: "{{slots}} slots per day for {{days}} days",
                    slots: validation.effectiveMinSlotsAcrossDays,
                    days: validation.effectiveAvailableDaysRequired
                  })}
                </Typography>
                <Chip
                  size="small"
                  label={`${validation.minSlotsForDisplay}/${validation.effectiveMinSlotsAcrossDays}`}
                  color={validation.hasMinSlotsAcrossDays ? "success" : "error"}
                  variant="outlined"
                  aria-label={
                    validation.hasMinSlotsAcrossDays
                      ? t("ROOSTER.PUBLIC-APP.MIN_SLOTS_REQUIREMENT_MET", {
                          defaultValue: `Minimum ${validation.minSlotsForDisplay} of ${validation.effectiveMinSlotsAcrossDays} slots per day, requirement met`
                        })
                      : t("ROOSTER.PUBLIC-APP.MIN_SLOTS_REQUIREMENT_NOT_MET", {
                          defaultValue: `Minimum ${validation.minSlotsForDisplay} of ${validation.effectiveMinSlotsAcrossDays} slots per day, requirement not met`
                        })
                  }
                />
              </Box>
            </Stack>
          </Box>
        </Menu>
      </Box>
    );
  }
};
